import app from 'firebase/app'
import firebase from 'firebase'
import { IUser } from '../types/users'
import { IUserSession } from '../types/models'
import firebaseConfig from './firebase/config'
import { createContext, useContext, useReducer } from 'react'
import { IFormLogin, IFormLoginRegistro } from '../types/forms'
import { AxiosClient, AxiosRegister, AxiosToken } from '../config/axios'

// TYPES
export type MessageType = 'error' | 'warning' | 'info'
type SessionContextType = {
  token: string | null
  // auth: any
  message: { text: string; type: MessageType } | null
  user: IUserSession | null
  autenticated?: boolean | null
  loading: boolean
  register?: (data: IFormLoginRegistro) => void
  login?: (data: IFormLogin) => void
  loginEmail?: (user: any) => void
  loginGoogle?: (user: any) => void
  loginFacebook?: (user: any) => void
  getUserData?: () => void
  logout?: () => void
  registervalidator?: (username: string) => Promise<boolean | undefined>
  loginvalidator?: (dataUser: string, password: string) => void
  getUserLoginProvider?: (data: IUser, username: string, password: string) => void
  changePasswordValidator?: (username: string) => Promise<IUser | undefined>
  changePasswordFirebase?: (dataUser: string) => void
  changePasswordPassport?: (user: IUser, password: string) => void
}

// TYPE ACTIONS
export enum SessionActions {
  SHOW_MESSAGE = 'SHOW MESSAGE',
  HIDE_MESSAGE = 'HIDE MESSAGE',
  REGISTER_DONE = 'REGISTER_DONE',
  REGISTER_ERROR = 'REGISTER_ERROR',
  GET_USER = 'GET_USER',
  LOGIN_DONE = 'LOGIN DONE',
  LOGIN_ERROR = 'LOGIN ERROR',
  LOGOUT = 'LOGOUT',
  GET_DATA_USER = 'GET_USER_DATA',
  GET_DATA_ERROR = 'GET_USER_ERROR',
  CHANGE_PASSWORD_SUCCESSFUL = 'CHANGE PASSWORD SUCCESSFUL',
  CHANGE_PASSWORD_ERROR = 'CHANGE PASSWORD ERROR',
}

// CONTEXT
const SessionContext = createContext<SessionContextType>(
  {} as SessionContextType
)

// REDUCER
const SessionReducer = (state: SessionContextType, action: any) => {
  switch (action.type) {
    case SessionActions.SHOW_MESSAGE:
      return { ...state, message: action.payload.message }
    case SessionActions.HIDE_MESSAGE:
      return { ...state, message: null }
    case SessionActions.LOGIN_DONE:
    case SessionActions.REGISTER_DONE:
      localStorage.setItem('token', action.payload.token)
      return {
        ...state,
        autenticated: true,
        message: null,
        token: action.payload.token,
        loading: false,
      }
    case SessionActions.GET_USER:
      return {
        ...state,
        autenticated: true,
        message: null,
        user: action.payload,
        loading: false,
      }
    case SessionActions.LOGOUT:
    case SessionActions.LOGIN_ERROR:
    case SessionActions.REGISTER_ERROR:
      localStorage.removeItem('token')
      return {
        ...state,
        autenticated: null,
        message: action.payload,
        token: null,
        user: null,
        loading: false,
      }
    case SessionActions.GET_DATA_USER:
      return {
        ...state,
        message: null,
        user: action.payload,
        loading: false,
      }
      case SessionActions.GET_DATA_ERROR:
        return {
          ...state,
          message: action.payload,
          user: null,
          loading: false,
        }
      case SessionActions.CHANGE_PASSWORD_SUCCESSFUL:
      return{
        ...state,
        autenticated: null,
        message: null,
        user: action.payload,
        loading: false,
      }
    case SessionActions.CHANGE_PASSWORD_ERROR:
      return{
        ...state,
        autenticated: null,
        message: action.payload,
        user: null,
        loading: false,
    }
    default:
      return state
  }
}

// PROVIDER
export const SessionContextProvider = (props: any): any => {
  if (!app.apps.length) {
    app.initializeApp(firebaseConfig)
  }

  const initialState: SessionContextType = {
    token: localStorage.getItem('token'),
    user: null,
    autenticated: null,
    message: null,
    loading: true,
  }
  const [state, dispatch] = useReducer(SessionReducer, initialState)

  // FUNCTIONS

  /**
   * This method is used to connect with backend, send data and register user
   * @param {IFormLoginRegistro} data
   * @return {void}
   */
  const register = async (data: IFormLoginRegistro) => {
    try {
      const response = await AxiosRegister.post('/auth/register', data)
      dispatch({ type: SessionActions.REGISTER_DONE, payload: response.data })
      getUserData()
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.REGISTER_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend, send data and login user
   * @param {IFormLogin} data
   * @return {void}
  */
  const login = async (data: IFormLogin) => {
    try {
      const response = await AxiosClient.post('/auth/login', data)
      dispatch({ type: SessionActions.LOGIN_DONE, payload: response.data })
      getUserData()
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend, send data user and login user with email
   * @param {Any} user
   * @return {void}
  */
  const loginEmail = async (user: any) => {
    try {
      const response = await AxiosRegister.post('/auth/login-email', {
        id: user.uid,
        email: user.email,
        user_name: user.email,
      })
      dispatch({ type: SessionActions.LOGIN_DONE, payload: response.data })
      getUserData()
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend, send data user and login user with google
   * @param {Any} user
   * @return {Object} user data
  */
  const loginGoogle = async (user: any) => {
    try {
      const displayName = user.displayName.split(' ');
      const arrayLastName = displayName.slice(1);
      const response = await AxiosRegister.post('/auth/login-google', {
        id: user.uid,
        email: user.email,
        /* user_name: user.email.split('@')[0], */
        user_name: user.email,
        first_name: displayName[0] || '',
        surname: arrayLastName.join(' '),
        profile_url: user.photoURL
      })
      dispatch({ type: SessionActions.LOGIN_DONE, payload: response.data })
      getUserData()
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend, send data user and login user with facebook
   * @param {Any} user
   * @return {Object} user data
  */
  const loginFacebook = async (user: any) => {
    try {
      const displayName = user.displayName.split(' ');
      const arrayLastName = displayName.slice(1);
      const response = await AxiosRegister.post('/auth/login-facebook', {
        id: user.uid,
        email: user.email,
        /* user_name: user.email.split('@')[0], */
        user_name: user.email,
        first_name: displayName[0] || '',
        surname: arrayLastName.join(' '),
        profile_url: user.photoURL
      })
      dispatch({ type: SessionActions.LOGIN_DONE, payload: response.data })
      getUserData()
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend and get user information
   * @return {Object} user data
  */
  const getUserData = async () => {
    const token = localStorage.getItem('token')
    if (token) {
      AxiosToken(token)
    }
    try {
      const response = await AxiosClient.get('/profile')
      dispatch({ type: SessionActions.GET_USER, payload: response.data })
    } catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend and logout user
   * @return {void}
  */
  const logout = () => {
    firebase.auth().signOut()
    dispatch({ type: SessionActions.LOGOUT })
  }

  /**
   * This method is used to validate if a user already exists to register
   * @return {Boolean}
  */
  const registervalidator = async (username: string) => {
    let user:IUser;
    try {
      user = (await AxiosClient.get(`user/user_name?user_name=${username}`)).data
      if(!user){
        user = (await AxiosClient.get(`user/email?email=${username}`)).data
      }
      if(user)
      {
        return true;  
      }
      return false;
    }   
    catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.GET_DATA_ERROR, payload: message })
    }
  }

  /**
   * This method is used to validate if a user already exists to login and consume the getUserLoginProvider method
   * @return {Object} user data
  */
  const loginvalidator = async (username: string, password: string) => {
    let response:IUser;
    try {
      response = (await AxiosClient.get(`user/user_name?user_name=${username}`)).data
      if(!response){
        response = (await AxiosClient.get(`user/email?email=${username}`)).data
      }
      if(!response){
        const message = {
          text: "La combinación de usuario y contraseña que ingresaste no se encuentra en nuestros registros.",
          type: 'error',
        }
        dispatch({ type: SessionActions.GET_DATA_ERROR, payload: message })
      }
      if(response)
      {
        getUserLoginProvider(response, username, password);
        dispatch({ type: SessionActions.GET_DATA_USER, payload: response })
        return response;  
      }
    }   
    catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.GET_DATA_ERROR, payload: message })
    }
  }

  /**
   * This method is used to validate what type of record the user has and login with method login or loginEmail
   * @return {Void}
  */
  const getUserLoginProvider = async (data: IUser, username: string, password: string) => 
  {
    if(data.login_provider === 'PASSPORT')
    {
      if (login) login({ username, password })
    }
    if(data.login_provider === 'EMAIL')
    {
      try {
        const result = await firebase.auth().signInWithEmailAndPassword(username, password)
        if(loginEmail) {
          loginEmail(result.user)
        }
      } catch (error:any) {
        const message = {
          text: "La combinación de usuario y contraseña que ingresaste no se encuentra en nuestros registros.",
          type: 'error',
        }
        dispatch({ type: SessionActions.LOGIN_ERROR, payload: message })
      }
    }
  }

  /**
   * This method is used to validate if a user already exists to register
   * @return {Object} user
  */
  const changePasswordValidator = async (username: string) => {
    let response:IUser;
    try {
      response = (await AxiosClient.get(`user/user_name?user_name=${username}`)).data
      if(!response){
        response = (await AxiosClient.get(`user/email?email=${username}`)).data
      }
      if(!response){
        const message = {
          text: "El usuario que ingresaste no se encuentra en nuestros registros.",
          type: 'error',
        }
        dispatch({ type: SessionActions.GET_DATA_ERROR, payload: message })
      }
      if(response)
      {
        dispatch({ type: SessionActions.GET_DATA_USER, payload: response })
      }
      return response;
    }   
    catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.GET_DATA_ERROR, payload: message })
    }
  }

  /**
   * This method is used to send an email through firebase and change the password
   * @return {Void}
  */
  const changePasswordFirebase = async (dataUser: string) => {
    try {
      const auth = firebase.auth();
      await auth.sendPasswordResetEmail(dataUser)
    }
    catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.CHANGE_PASSWORD_ERROR, payload: message })
    }
  }

  /**
   * This method is used to connect with backend and change the password
   * @return {Void}
  */
  const changePasswordPassport = async (user: IUser, password: string) => 
  {
    try 
    {
      const response = await AxiosClient.put('/user/password', {user, password})
      dispatch({ type: SessionActions.CHANGE_PASSWORD_SUCCESSFUL, payload: response.data })
    } 
    catch (error:any) {
      const message = {
        text: error.response.data.message,
        type: 'error',
      }
      dispatch({ type: SessionActions.CHANGE_PASSWORD_ERROR, payload: message })
    }
  }

  return (
    <SessionContext.Provider
      value={{
        token: state.token,
        autenticated: state.autenticated,
        user: state.user,
        message: state.message,
        loading: state.loading,
        register,
        login,
        loginEmail,
        loginGoogle,
        loginFacebook,
        getUserData,
        logout,
        registervalidator,
        loginvalidator,
        getUserLoginProvider,
        changePasswordValidator,
        changePasswordFirebase,
        changePasswordPassport
      }}
    >
      {props.children}
    </SessionContext.Provider>
  )
}

// EXPORT HOOK
export const useSession = (): SessionContextType => useContext(SessionContext)